import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import i18n from '@/i18n/config';

interface Profile {
  id: string;
  username: string | null;
  avatar_url: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  isAdmin: boolean;
  isModerator: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  updateUsername: (newUsername: string) => Promise<{ error: any }>;
  updatePassword: (newPassword: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isModerator, setIsModerator] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    try {
      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileData) {
        setProfile(profileData);
        console.log('👤 Profile loaded:', profileData.username);
      }

      // Check user roles (admin and moderator)
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (rolesError) {
        console.error('⚠️ Error fetching roles:', rolesError);
      }

      const roles = rolesData?.map(r => r.role) || [];
      setIsAdmin(roles.includes('admin'));
      setIsModerator(roles.includes('moderator'));
      console.log('🛡️ User roles:', roles);
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  useEffect(() => {
    console.log('🔐 Auth initializing...');
    let initialLoadDone = false;
    
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Defer profile fetch to avoid blocking auth state change
          setTimeout(() => {
            fetchProfile(session.user.id);
          }, 0);
        } else {
          setProfile(null);
          setIsAdmin(false);
          setIsModerator(false);
        }
        
        // Only set loading=false from listener AFTER initial load is done
        // This prevents a race condition where ProtectedRoute sees isAdmin=false
        if (initialLoadDone) {
          setLoading(false);
        }
      }
    );

    // Initialize auth with async/await and timeout
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Await fetchProfile with 10s timeout
          await Promise.race([
            fetchProfile(session.user.id),
            new Promise((resolve) => setTimeout(resolve, 10000))
          ]);
        }
        
        console.log('✅ Auth ready');
      } catch (error) {
        console.error('❌ Auth initialization error:', error);
      } finally {
        initialLoadDone = true;
        setLoading(false);
      }
    };

    initAuth();

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      return { error: null };
    } catch (error: any) {
      toast.error(error.message || i18n.t('errors:loginFailed'));
      return { error };
    }
  };

  const signUp = async (email: string, password: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
        },
      });

      if (error) throw error;

      return { error: null };
    } catch (error: any) {
      if (error.message?.includes('already registered')) {
        toast.error(i18n.t('errors:alreadyRegistered'));
      } else {
        toast.error(error.message || i18n.t('errors:accountCreationFailed'));
      }
      return { error };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.warn('SignOut warning (session may already be invalid):', error);
    } finally {
      // ALWAYS clear local state, even if signOut fails
      setUser(null);
      setSession(null);
      setProfile(null);
      setIsAdmin(false);
      setIsModerator(false);
      
      // Manually clear Supabase tokens from localStorage
      Object.keys(localStorage).forEach(key => {
        if (key.includes('supabase') || key.includes('sb-')) {
          localStorage.removeItem(key);
        }
      });
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
    }
  };

  const updateUsername = async (newUsername: string) => {
    if (!user) return { error: { message: 'No user logged in' } };
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ username: newUsername })
        .eq('id', user.id);
        
      if (error) throw error;
      
      await refreshProfile();
      toast.success(i18n.t('errors:usernameUpdated'));
      return { error: null };
    } catch (error: any) {
      return { error };
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) throw error;
      
      toast.success(i18n.t('errors:passwordUpdated'));
      return { error: null };
    } catch (error: any) {
      toast.error(error.message || i18n.t('errors:passwordChangeFailed'));
      return { error };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        isAdmin,
        isModerator,
        loading,
        signIn,
        signUp,
        signOut,
        refreshProfile,
        updateUsername,
        updatePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

const defaultAuthContext: AuthContextType = {
  user: null,
  session: null,
  profile: null,
  isAdmin: false,
  isModerator: false,
  loading: true,
  signIn: async () => ({ error: new Error('AuthProvider not mounted') }),
  signUp: async () => ({ error: new Error('AuthProvider not mounted') }),
  signOut: async () => {},
  refreshProfile: async () => {},
  updateUsername: async () => ({ error: { message: 'AuthProvider not mounted' } }),
  updatePassword: async () => ({ error: { message: 'AuthProvider not mounted' } }),
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    console.warn('useAuth called outside AuthProvider — returning safe defaults (HMR?)');
    return defaultAuthContext;
  }
  return context;
}
